import { formatChartDate } from './dateUtils';

/**
 * Compute X-axis time domain for My Progress page.
 * 
 * CRITICAL: Domain is FIXED based on time window selection.
 * - 12M = Today minus 12 months → Today
 * - 24M = Today minus 24 months → Today
 * - 36M = Today minus 36 months → Today
 * - All = Earliest data point → Today
 * 
 * Line data includes points OUTSIDE the window for continuity
 * (line enters from left edge if there's earlier data).
 * 
 * @param {Array} results - Result records with date field
 * @param {Array} treatments - Treatment records
 * @param {string} country - User's country for date formatting
 * @param {string} timeWindow - '12m', '24m', '36m', or 'all'
 * @returns {Object|null} Domain config for X-axis
 */
export const computePageDomain = (results, treatments = [], country = 'AU', timeWindow = 'all') => {
  if (!results || results.length === 0) return null;
  
  // Collect all result dates
  const resultDates = results
    .map(r => r.date)
    .filter(Boolean)
    .map(date => ({
      date,
      timestamp: new Date(date).getTime()
    }))
    .filter(d => !isNaN(d.timestamp))
    .sort((a, b) => a.timestamp - b.timestamp);
  
  if (resultDates.length === 0) return null;
  
  // For 'all' view: end at TODAY (live graph that moves with time)
  // For 12m/24m/36m: end at TODAY
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  const todayTs = today.getTime();
  const mostRecentResultTs = resultDates[resultDates.length - 1].timestamp;
  
  let domainEndTs;
  let domainStartTs;
  
  if (timeWindow === 'all') {
    // For "All" - from earliest data to TODAY (live graph)
    domainEndTs = todayTs;
    
    const treatmentStarts = (treatments || [])
      .map(t => t.start_date)
      .filter(Boolean)
      .map(date => new Date(date).getTime())
      .filter(ts => !isNaN(ts));
    
    const earliestResult = resultDates[0].timestamp;
    const earliestTreatment = treatmentStarts.length > 0 ? Math.min(...treatmentStarts) : earliestResult;
    domainStartTs = Math.min(earliestResult, earliestTreatment);
  } else {
    // For 12m, 24m, 36m - fixed window from TODAY
    domainEndTs = todayTs;
    
    const months = {
      '12m': 12,
      '24m': 24,
      '36m': 36
    }[timeWindow] || 36;
    
    const windowStart = new Date(today);
    windowStart.setMonth(windowStart.getMonth() - months);
    windowStart.setHours(0, 0, 0, 0);
    domainStartTs = windowStart.getTime();
  }
  
  // Last result date (where the line stops)
  const lastResultTs = resultDates[resultDates.length - 1].timestamp;
  
  const minDate = new Date(domainStartTs).toISOString().split('T')[0];
  const maxDate = new Date(domainEndTs).toISOString().split('T')[0];
  
  // Unique sorted data points (results only)
  const uniqueDates = [...new Set(resultDates.map(d => d.date))];
  
  const dataPoints = uniqueDates.map(date => ({
    date: date,
    timestamp: new Date(date).getTime(),
    label: formatChartDate(date, country)
  }));
  
  return {
    minDate,
    maxDate,
    domainStartTs,
    domainEndTs,
    lastResultTs,
    dataPoints,
    timeWindow,
    margin: { left: 32, right: 0 }
  };
};

/**
 * @deprecated Use computePageDomain instead
 */
export const computeResultsDomain = (results, country = 'AU') => {
  return computePageDomain(results, [], country, 'all');
};

/**
 * Calculate treatment bar position within the results domain.
 * Treatments align visually but do NOT modify the domain.
 * 
 * @param {string} startDate - Treatment start date
 * @param {string} endDate - Treatment end date (null = ongoing)
 * @param {Object} domain - The results domain config
 * @returns {Object} Position style { left, width, visible }
 */
export const calculateTreatmentPosition = (startDate, endDate, domain) => {
  if (!domain || !startDate) {
    return { left: '0%', width: '0%', visible: false };
  }
  
  const { domainStartTs, domainEndTs } = domain;
  const domainRange = domainEndTs - domainStartTs;
  
  if (domainRange <= 0) {
    return { left: '0%', width: '100%', visible: true };
  }
  
  const startTs = new Date(startDate).getTime();
  const endTs = endDate ? new Date(endDate).getTime() : Date.now();
  
  // Treatment completely outside domain - still show at edges
  if (endTs < domainStartTs) {
    // Ended before domain starts - show at left edge
    return { left: '0%', width: '2%', visible: true };
  }
  
  if (startTs > domainEndTs) {
    // Starts after domain ends - show at right edge
    return { left: '98%', width: '2%', visible: true };
  }
  
  // Clamp to domain bounds for positioning
  const clampedStart = Math.max(startTs, domainStartTs);
  const clampedEnd = Math.min(endTs, domainEndTs);
  
  // Calculate percentage positions
  const startPos = ((clampedStart - domainStartTs) / domainRange) * 100;
  const endPos = ((clampedEnd - domainStartTs) / domainRange) * 100;
  const width = Math.max(2, endPos - startPos);
  
  return {
    left: `${Math.max(0, startPos)}%`,
    width: `${Math.min(100 - startPos, width)}%`,
    visible: true
  };
};
